import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Import services and components
import listingService from '../services/listingService';
import certificateService from '../services/certificateService';
import ListingForm from '../components/ListingForm';
import ImageUploader from '../components/ImageUploader';
import LocationPicker from '../components/LocationPicker';

function EditListingPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [listing, setListing] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [location, setLocation] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [certificateVerified, setCertificateVerified] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch the listing to edit and check certificate status
  useEffect(() => {
    if (!user) return;
    
    if (user.role !== 'donor') {
      // Redirect non-donors to dashboard
      navigate('/dashboard');
      return;
    }

    // Fetch listing data
    const fetchListingData = async () => {
      setLoading(true);
      try {
        const response = await listingService.getListingById(id);
        
        if (response && response.listing) {
          console.log('Fetched listing data:', response.listing);
          
          // Set listing data
          setListing(response.listing);
          
          // Set location if available
          if (response.listing.location && 
             (response.listing.location.lat || response.listing.location.lng)) {
            setLocation(response.listing.location);
          }
          
          // Set image preview if available
          if (response.listing.imageUrl) {
            setImagePreview(response.listing.imageUrl);
          }
        } else {
          setError('Listing not found or you do not have permission to edit it.');
          setTimeout(() => navigate('/manage-listings'), 2000);
        }
      } catch (err) {
        console.error('Error fetching listing:', err);
        setError('Failed to load listing details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    // Check certificate status
    const checkCertificateStatus = async () => {
      try {
        const isValid = await certificateService.hasValidCertificate();
        setCertificateVerified(isValid);
      } catch (err) {
        console.error('Error checking certificate status:', err);
        setCertificateVerified(false);
      }
    };
    
    fetchListingData();
    checkCertificateStatus();
  }, [user, id, navigate]);

  // Handle image change from ImageUploader
  const handleImageChange = (file, preview) => {
    setImageFile(file);
    setImagePreview(preview);
  };

  // Handle location change from LocationPicker
  const handleLocationChange = (locationData) => {
    setLocation(locationData);
  };

  // Handle form submission
  const handleSubmit = async (formData) => {
    // Clear any previous messages
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    try {
      // Validate location if changed
      if (location && (!location.lat || !location.lng)) {
        throw new Error('Please provide a valid location on the map');
      }

      // Create data object for update
      const updateData = {
        ...formData,
        // Convert fields to match backend expectations
        category: formData.foodType,
        expiry_date: formData.expiryDate,
        quantity_unit: formData.unit,
        storage_requirements: formData.storageRequirements
      };
      
      // Add location if provided
      if (location) {
        updateData.location = JSON.stringify(location);
      }
      
      // If we have a new image file, we need to use FormData
      if (imageFile) {
        const formDataToSend = new FormData();
        
        // Add all form fields
        Object.keys(updateData).forEach(key => {
          formDataToSend.append(key, updateData[key]);
        });
        
        // Add image
        formDataToSend.append('image', imageFile);
        
        // Send to API using listingService
        await listingService.updateListing(id, formDataToSend);
      } else {
        // Regular JSON update without image
        await listingService.updateListing(id, updateData);
      }
      
      setSuccess('Listing updated successfully!');
      
      // Redirect to listings management page after success
      setTimeout(() => {
        navigate('/manage-listings');
      }, 1500);
      
    } catch (err) {
      console.error('Error updating listing:', err);
      setError(err.message || 'Failed to update listing. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // If user not authenticated or listing not loaded yet, show loading indicator
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="max-w-5xl mx-auto p-4">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4">
          <p>{error || "Listing not found"}</p>
          <button 
            onClick={() => navigate('/manage-listings')}
            className="mt-4 bg-primary-600 text-white px-4 py-2 rounded"
          >
            Back to Manage Listings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="md:flex md:items-center md:justify-between mb-8">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">Edit Food Listing</h2>
        </div>
      </div>

      {/* Certificate warning */}
      {user.role === 'donor' && !certificateVerified && (
        <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Your food safety certificate is still pending verification. Your listing will be visible to recipients only after your certificate is approved.
                <a href="/upload-certificate" className="font-medium underline text-yellow-700 hover:text-yellow-600 ml-1">
                  Upload a certificate
                </a>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* Success Message */}
      {success && (
        <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="grid grid-cols-1 gap-y-8 gap-x-6 md:grid-cols-2">
            {/* Left column - form */}
            <div className="space-y-8">
              <h3 className="text-lg font-medium leading-6 text-gray-900">Listing Details</h3>
              <ListingForm
                initialData={{
                  title: listing.title,
                  description: listing.description,
                  quantity: listing.quantity,
                  unit: listing.unit || listing.quantity_unit,
                  foodType: listing.foodType || listing.category,
                  expiryDate: listing.expiryDate || listing.expiry_date,
                  pickupInstructions: listing.pickupInstructions || listing.pickup_instructions,
                  allergens: listing.allergens,
                  storageRequirements: listing.storageRequirements || listing.storage_requirements
                }}
                onSubmit={handleSubmit}
                submitButtonText="Update Listing"
                loading={isSubmitting}
              />
            </div>
            
            {/* Right column - image and location */}
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900">Food Image</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Update or leave the current image
                </p>
                {imagePreview && (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-2">Current image:</p>
                    <img 
                      src={imagePreview} 
                      alt="Current listing" 
                      className="h-40 w-auto object-cover rounded-md"
                    />
                  </div>
                )}
                <ImageUploader
                  onImageChange={handleImageChange}
                  maxSizeMB={2}
                  label="Update Food Image"
                />
              </div>
              
              <div>
                <h3 className="text-lg font-medium leading-6 text-gray-900">Pickup Location</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Update or leave the current pickup location
                </p>
                <LocationPicker 
                  onLocationChange={handleLocationChange}
                  initialLocation={location}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditListingPage;
